import { useState } from 'react';

import {
  Card,
  LineChart,
  LineChartSkeleton,
  SelectDropdown,
} from '@opositandopn/ui';
import { FaRunning } from 'react-icons/fa';
import { IoRibbonOutline, IoStopwatchOutline } from 'react-icons/io5';
import { PiBarbell } from 'react-icons/pi';

import { Page } from '../../components/shared/page/Page';
import { ValueCard } from '../../components/shared/value-card/ValueCard';
import { useProfile } from '../../context/ProfileContext';
import {
  TimeSpanString,
  useGetPhysicalTestsMetrics,
} from '../../features/physical-tests/api/usePhysicalTests';
import { SelectGenderDialog } from '../../features/physical-tests/components/select-gender-dialog/SelectGenderDialog';
import { formatSortDayDate } from '../../utils/date';

import styles from './PhysicalTestsProgressPage.module.scss';

type Range = 'thisWeek' | 'twoWeeks' | 'oneMonth' | 'threeMonths';

const RANGE_LABELS: Record<Range, string> = {
  thisWeek: 'Últimos siete días',
  twoWeeks: 'Últimas dos semanas',
  oneMonth: 'Último mes',
  threeMonths: 'Últimos tres meses',
};

function getFromDate(range: Range): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  switch (range) {
    case 'thisWeek':
      d.setDate(d.getDate() - 7);
      return d;
    case 'twoWeeks':
      d.setDate(d.getDate() - 14);
      return d;
    case 'oneMonth':
      d.setMonth(d.getMonth() - 1);
      return d;
    case 'threeMonths':
      d.setMonth(d.getMonth() - 3);
      return d;
  }
}

function timeSpanToSeconds(value: TimeSpanString): number {
  const [head, fraction] = value.split('.');
  const parts = head.split(':').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) {
    return 0;
  }
  const [h, m, s] = parts;
  const hundredths = fraction ? Number(fraction.slice(0, 2)) || 0 : 0;
  return h * 3600 + m * 60 + s + hundredths / 100;
}

function sortAsc<T extends { date: Date }>(sessions: T[] | undefined): T[] {
  return sessions
    ? [...sessions].sort((a, b) => a.date.getTime() - b.date.getTime())
    : [];
}

function PhysicalTestsProgressPage() {
  const { profile } = useProfile();
  const isMale = profile?.gender === 'male';

  const [selectedRange, setSelectedRange] = useState<Range>('thisWeek');
  const fromDate = getFromDate(selectedRange);

  const { data: metrics, isLoading } = useGetPhysicalTestsMetrics(
    { fromDate },
    !!profile?.id,
  );

  const obstacleCourseSessions = sortAsc(metrics?.obstacleCourse);
  const pullUpsSessions = sortAsc(metrics?.pullUps);
  const hangSessions = sortAsc(metrics?.hang);
  const runSessions = sortAsc(metrics?.run);

  const bestObstacleCourseSeconds = obstacleCourseSessions.length
    ? Math.min(...obstacleCourseSessions.map((s) => timeSpanToSeconds(s.time)))
    : 0;
  const bestPullUpsReps = pullUpsSessions.length
    ? Math.max(...pullUpsSessions.map((s) => s.reps))
    : 0;
  const bestHangSeconds = hangSessions.length
    ? Math.max(...hangSessions.map((s) => timeSpanToSeconds(s.time)))
    : 0;
  const bestRunPaceMinutes = runSessions.length
    ? Math.min(...runSessions.map((s) => timeSpanToSeconds(s.pace))) / 60
    : 0;

  const bestObstacleCoursePoints = obstacleCourseSessions.length
    ? Math.max(...obstacleCourseSessions.map((s) => s.points))
    : 0;
  const bestPullUpsPoints = pullUpsSessions.length
    ? Math.max(...pullUpsSessions.map((s) => s.points))
    : 0;
  const bestHangPoints = hangSessions.length
    ? Math.max(...hangSessions.map((s) => s.points))
    : 0;
  const bestRunPoints = runSessions.length
    ? Math.max(...runSessions.map((s) => s.points))
    : 0;

  const bestStrengthPoints = isMale ? bestPullUpsPoints : bestHangPoints;
  const averagePoints =
    (bestObstacleCoursePoints + bestStrengthPoints + bestRunPoints) / 3;

  return (
    <Page title="Progreso">
      <div className={styles['filters']}>
        <SelectDropdown
          onSelect={(value: string | number) =>
            setSelectedRange(value as Range)
          }
          options={(Object.keys(RANGE_LABELS) as Range[]).map((value) => ({
            value,
            label: RANGE_LABELS[value],
            selected: selectedRange === value,
          }))}
        />
      </div>

      <div className={styles['row']}>
        <ValueCard
          className={styles['value-card']}
          icon={<IoStopwatchOutline />}
          title="Mejor tiempo en circuito"
          value={Number(bestObstacleCourseSeconds.toFixed(2))}
          format={{
            style: 'unit',
            unit: 'second',
            unitDisplay: 'short',
            maximumFractionDigits: 2,
          }}
        />
        {isMale ? (
          <ValueCard
            className={styles['value-card']}
            icon={<PiBarbell />}
            title="Máximas dominadas"
            value={bestPullUpsReps}
          />
        ) : (
          <ValueCard
            className={styles['value-card']}
            icon={<PiBarbell />}
            title="Mejor tiempo en suspensión en barra"
            value={Math.round(bestHangSeconds)}
            format={{
              style: 'unit',
              unit: 'second',
              unitDisplay: 'short',
              maximumFractionDigits: 0,
            }}
          />
        )}
        <ValueCard
          className={styles['value-card']}
          icon={<FaRunning />}
          title="Mejor ritmo de carrera"
          value={Number(bestRunPaceMinutes.toFixed(2))}
          format={{
            style: 'unit',
            unit: 'minute-per-kilometer',
            unitDisplay: 'short',
            maximumFractionDigits: 2,
          }}
        />
        <ValueCard
          className={styles['value-card']}
          icon={<IoRibbonOutline />}
          title="Puntuación media"
          value={Number(averagePoints.toFixed(2))}
        />
      </div>

      <div className={styles['row']}>
        <Card
          className={styles['chart-card']}
          icon={<IoStopwatchOutline />}
          title="Circuito de agilidad"
        >
          {isLoading ? (
            <LineChartSkeleton labels={1} />
          ) : obstacleCourseSessions.length === 0 ? (
            <p className={styles['empty']}>Sin datos</p>
          ) : (
            <LineChart
              className={styles['chart']}
              labels={obstacleCourseSessions.map((s) =>
                formatSortDayDate(s.date),
              )}
              dataset={[
                {
                  label: 'Segundos',
                  values: obstacleCourseSessions.map((s) =>
                    timeSpanToSeconds(s.time),
                  ),
                },
              ]}
            />
          )}
        </Card>
      </div>

      <div className={styles['row']}>
        <Card
          className={styles['chart-card']}
          icon={<PiBarbell />}
          title={isMale ? 'Dominadas' : 'Suspensión en barra'}
        >
          {isLoading ? (
            <LineChartSkeleton labels={1} />
          ) : isMale ? (
            pullUpsSessions.length === 0 ? (
              <p className={styles['empty']}>Sin datos</p>
            ) : (
              <LineChart
                className={styles['chart']}
                labels={pullUpsSessions.map((s) => formatSortDayDate(s.date))}
                dataset={[
                  {
                    label: 'Repeticiones',
                    values: pullUpsSessions.map((s) => s.reps),
                  },
                ]}
              />
            )
          ) : hangSessions.length === 0 ? (
            <p className={styles['empty']}>Sin datos</p>
          ) : (
            <LineChart
              className={styles['chart']}
              labels={hangSessions.map((s) => formatSortDayDate(s.date))}
              dataset={[
                {
                  label: 'Segundos',
                  values: hangSessions.map((s) => timeSpanToSeconds(s.time)),
                },
              ]}
            />
          )}
        </Card>
      </div>

      <div className={styles['row']}>
        <Card
          className={styles['chart-card']}
          icon={<FaRunning />}
          title="Ritmo de carrera"
        >
          {isLoading ? (
            <LineChartSkeleton labels={1} />
          ) : runSessions.length === 0 ? (
            <p className={styles['empty']}>Sin datos</p>
          ) : (
            <LineChart
              className={styles['chart']}
              labels={runSessions.map((s) => formatSortDayDate(s.date))}
              dataset={[
                {
                  label: 'Minutos por km',
                  values: runSessions.map((s) =>
                    Number((timeSpanToSeconds(s.pace) / 60).toFixed(2)),
                  ),
                },
              ]}
            />
          )}
        </Card>
      </div>

      <SelectGenderDialog />
    </Page>
  );
}

export { PhysicalTestsProgressPage };
